import { useEffect, useState, useRef } from "react";
import {
  Button,
  Box,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  TextField,
  Typography,
  Popover,
  Checkbox,
  Chip,
  Grid,
  Autocomplete,
  CircularProgress,
  Menu,
  Divider,
  MenuItem,
  Collapse,
} from "@mui/material";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import { useMediaQuery, useTheme } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Print as PrintIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import {
  IndentListApi,
  IndentDelete,
  ApproveIndent,
} from "../../../Services/Services";
import InfiniteScroll from "react-infinite-scroll-component";
import { useNavigate } from "react-router";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import { handlePrintIndent } from "./IndentPrint";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

pdfMake.vfs = pdfFonts.vfs;

const IndentList = () => {
  const [indents, setIndents] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [search, setSearch] = useState("");
  const [searchIndent, setSearchIndent] = useState("");
  const [searchItem, setSearchItem] = useState("");
  const [searchUOM, setSearchUOM] = useState("");
  const [searchProject, setSearchProject] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [indentToDelete, setIndentToDelete] = useState(null);
  const [approveAnchorEl, setApproveAnchorEl] = useState(null);
  const [indentToApprove, setIndentToApprove] = useState(null);
  const [remarkInput, setRemarkInput] = useState("");
  const [showRemarkForIndentId, setShowRemarkForIndentId] = useState(null);
  const [actionType, setActionType] = useState(null); // ✅ new state
  
  const [showCustomDateInputs, setShowCustomDateInputs] = useState(false);
  const [tempCustomDateRange, setTempCustomDateRange] = useState({
    from: "",
    to: "",
  });
  const [dateMenuAnchor, setDateMenuAnchor] = useState(null);
  const [customDateRange, setCustomDateRange] = useState({
    from: "",
    to: "",
  });
  const [filters, setFilters] = useState({
    date_range: "today", // ✅ default value
  });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [expandedIndent, setExpandedIndent] = useState(null);

  const navigate = useNavigate();

  const getDateRangeDisplay = (rangeType) => {
    const now = new Date();
    const formatDate = (date) => {
      const day = date.getDate().toString().padStart(2, "0");
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    };

    switch (rangeType) {
      case "today":
        return formatDate(now);
      case "yesterday":
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        return formatDate(yesterday);
      case "this_week":
        const weekStart = new Date(now);
        weekStart.setDate(weekStart.getDate() - weekStart.getDay() + 1);
        return `${formatDate(weekStart)} to ${formatDate(now)}`;
      case "previous_week":
        const prevWeekEnd = new Date(now);
        prevWeekEnd.setDate(prevWeekEnd.getDate() - prevWeekEnd.getDay());
        const prevWeekStart = new Date(prevWeekEnd);
        prevWeekStart.setDate(prevWeekStart.getDate() - 6);
        return `${formatDate(prevWeekStart)} to ${formatDate(prevWeekEnd)}`;
      case "this_month":
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        return `${formatDate(monthStart)} to ${formatDate(now)}`;
      case "current_year":
        const yearStart = new Date(now.getFullYear(), 0, 1);
        return `${formatDate(yearStart)} to ${formatDate(now)}`;
      case "financial_year":
        let fyStart;
        if (now.getMonth() >= 3) {
          fyStart = new Date(now.getFullYear(), 3, 1);
        } else {
          fyStart = new Date(now.getFullYear() - 1, 3, 1);
        }
        return `${formatDate(fyStart)} to ${formatDate(now)}`;
      default:
        return "";
    }
  };

  const formatDateDisplay = (dateString) => {
    if (!dateString) return "";
    const [year, month, day] = dateString.split("-");
    return `${day}-${month}-${year}`;
  };

  // const handleReset = () => {
  //   setFilters({
  //     project_name: "",
  //     party: "",
  //     division: "",
  //     date_range: "this_month",
  //   });
  // }

  const handleReset = () => {
    setFilters({
      date_range: "today",
    });
    setSearchIndent("");
    setSearchProject("");
    setCustomDateRange({ from: "", to: "" });
    setTempCustomDateRange({ from: "", to: "" }); // ✅ Add
    setShowCustomDateInputs(false); // ✅ Add
    fetchIndents(1, {
      indent_no: "",
      project_name: "",
      date_range: "this_month",
      from_date: "",
      to_date: "",
    });
  };

  // ✅ Handle Custom Date Range Apply
  const handleApplyCustomDateRange = () => {
    if (tempCustomDateRange.from && tempCustomDateRange.to) {
      setCustomDateRange({
        from: tempCustomDateRange.from,
        to: tempCustomDateRange.to,
      });
      setFilters((prev) => ({ ...prev, date_range: "custom" }));
      setDateMenuAnchor(null);
      setShowCustomDateInputs(false);
    } else {
      toast.warning("Please select both From and To dates");
    }
  };

  // ✅ Handle Cancel Custom Date Range
  const handleCancelCustomDateRange = () => {
    setTempCustomDateRange({ from: "", to: "" });
    setShowCustomDateInputs(false);
  };

  const fetchIndents = async (pageNum = 1, filters = {}) => {
    const res = await IndentListApi(pageNum, filters);
    if (pageNum === 1) {
      setIndents(res.results);
    } else {
      setIndents((prev) => [...prev, ...res.results]);
    }
    setHasMore(res.next !== null);
    setPage(pageNum);
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setPage(1);
      fetchIndents(1, {
        indent_no: searchIndent,
        project_name: searchProject?.project_name || searchProject || "",
        date_range: filters.date_range || "this_month",
        from_date: customDateRange.from || "",
        to_date: customDateRange.to || "",
      });
    }, 500);

    return () => clearTimeout(handler);
  }, [searchIndent, searchProject, filters.date_range, customDateRange]);

  const loadMore = () => {
    const nextPage = page + 1;
    fetchIndents(nextPage, {
      indent_no: searchIndent,
      project_name: searchProject?.project_name || searchProject || "",
      date_range: filters.date_range || "this_month",
      from_date: customDateRange.from || "",
      to_date: customDateRange.to || "",
    });
    setPage(nextPage);
  };
  const filteredIndents = indents.filter((indent) => {
    const projectMatch = searchProject
      ? indent?.project_name
          ?.toLowerCase()
          .includes(searchProject.toLowerCase())
      : true;

    const indentNoMatch = searchItem
      ? indent?.indent_no?.toLowerCase().includes(searchItem.toLowerCase())
      : true;

    const uomMatch = searchUOM
      ? indent.items?.some((item) =>
          item.uom?.toLowerCase().includes(searchUOM.toLowerCase()),
        )
      : true;

    return projectMatch && indentNoMatch && uomMatch;
  });

  const handleApprove = async (indent) => {
    const approvedById = Number(localStorage.getItem("user_id"));
    if (!approvedById) {
      toast.error("Approver ID not found.");
      return;
    }

    try {
      const updated = await ApproveIndent(
        indent.indent_id,
        approvedById,
        indent.status,
        remarkInput, // ✅ remarks bhej do
      );

      setIndents((prev) =>
        prev.map((item) =>
          item.indent_id === indent.indent_id ? updated : item,
        ),
      );

      // ✅ Status ke basis par toast message
      if (updated.status === "Approved") {
        toast.success("Indent approved successfully!");
      } else if (updated.status === "Rejected") {
        toast.error("Indent rejected successfully!");
      } else {
        toast.info("Indent status updated!");
      }
    } catch (error) {
      console.error("Approval Toggle Error:", error);
      toast.error("Failed to update indent status.");
    }
  };

  const handleClick = () => {
    navigate("/dashboard/indent-form");
  };

  const handleDelete = async (indentId) => {
    if (!indentId) {
      console.error("No indent ID provided!");
      toast.error("Cannot delete: Indent ID is missing.");
      return;
    }

    try {
      await IndentDelete(indentId);
      setIndents((prev) =>
        prev.filter((indent) => indent.indent_id !== indentId),
      ); // match the key here too!
      toast.success("Indent deleted successfully!");
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Failed to delete indent. Please try again.");
    }
  };

  const handleEdit = (indent) => {
    navigate("/dashboard/indent-form", {
      state: {
        data: {
          indent_id: indent.indent_id, // ✅ Include ID for update
          indent_no: indent.indent_no,
          indent_type: indent.indent_type,
          form_remarks: indent.form_remarks,
          division: indent.division,
          department_name: indent.department_name || "",
          project: indent.project,
          created_by: indent.created_by || localStorage.getItem("user_id"),
          items: indent.items,
        },
      },
    });
  };
  const userRole = localStorage.getItem("role"); // "admin" ya "staff"

  function formatOnlyDate(dateTimeString) {
    if (!dateTimeString) return "N/A";

    // Take only the date part (before T)
    const datePart = dateTimeString.split("T")[0]; // "2025-12-02"

    const [year, month, day] = datePart.split("-");

    return `${day}-${month}-${year}`;
  }

  useEffect(() => {
    document.body.style.overflow = isMobile ? "auto" : "hidden";

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isMobile]);
  return (
    <Box>
      <Box
        display="flex"
        flexWrap="wrap"
        alignItems="center"
        sx={{
          mt: -3,
          pt: -3,
          mb: 1,
          gap: 1,
        }}
      >
        <Grid container spacing={2} alignItems="center">
          {/* Date Range Dropdown */}
          <Grid item xs={12} sm={2.5}>
            <TextField
              fullWidth
              label="Date Range"
              value={
                filters.date_range === "custom" &&
                customDateRange.from &&
                customDateRange.to
                  ? `${formatDateDisplay(customDateRange.from)} to ${formatDateDisplay(customDateRange.to)}`
                  : filters.date_range === "today"
                    ? "Today"
                    : filters.date_range === "yesterday"
                      ? "Yesterday"
                      : filters.date_range === "this_week"
                        ? "This Week"
                        : filters.date_range === "previous_week"
                          ? "Previous Week"
                          : filters.date_range === "this_month"
                            ? "This Month"
                            : filters.date_range === "current_year"
                              ? "Current Year"
                              : filters.date_range === "financial_year"
                                ? "Financial Year"
                                : "Select Date Range"
              }
              onClick={(e) => setDateMenuAnchor(e.currentTarget)}
              size="small"
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <IconButton size="small" edge="end">
                    <ArrowDropDownIcon />
                  </IconButton>
                ),
              }}
              sx={{
                cursor: "pointer",
                fontWeight: "bold",
                "& .MuiInputBase-input": {
                  fontSize: "0.8rem",
                  padding: "4px 8px",
                },
                "& .MuiInputBase-root": {
                  height: 30,
                  borderRadius: 0,
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.6rem",
                },
              }}
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="Search by Indent No"
              value={searchIndent}
              onChange={(e) => setSearchIndent(e.target.value)}
              size="small"
              sx={{
                "& .MuiInputBase-root": {
                  height: 30,
                  fontSize: "0.7rem",
                  borderRadius: 0,
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.6rem",
                },
              }}
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="Search by Project"
              value={searchProject}
              onChange={(e) => setSearchProject(e.target.value)}
              size="small"
              sx={{
                "& .MuiInputBase-root": {
                  height: 30,
                  fontSize: "0.7rem",
                  borderRadius: 0,
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.6rem",
                },
              }}
            />
          </Grid>

          <Grid item xs={6} sm={1.5}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              size="small"
              onClick={handleReset}
              sx={{
                height: 30,
                fontSize: "0.6rem",
                borderRadius: 0,
                whiteSpace: "nowrap",
              }}
            >
              Reset
            </Button>
          </Grid>

          <Grid item xs={6} sm={1.5}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              size="small"
              onClick={handleClick}
              startIcon={<AddIcon sx={{ fontSize: 18 }} />}
              sx={{
                height: 30,
                fontSize: "0.6rem",
                borderRadius: 0,
                whiteSpace: "nowrap",
              }}
            >
              Indent
            </Button>
          </Grid>
        </Grid>

        {/* Date Range Menu - UPDATED */}
        <Menu
          anchorEl={dateMenuAnchor}
          open={Boolean(dateMenuAnchor)}
          onClose={() => {
            setDateMenuAnchor(null);
            setShowCustomDateInputs(false);
            setTempCustomDateRange({ from: "", to: "" });
          }}
          PaperProps={{
            sx: {
              width: 350,
              maxHeight: 500,
              "& .MuiTypography-root": {
                fontSize: "0.65rem",
              },
            },
          }}
        >
          {[
            { key: "today", label: "Today" },
            { key: "yesterday", label: "Yesterday" },
            { key: "this_week", label: "This Week" },
            { key: "previous_week", label: "Previous Week" },
            { key: "this_month", label: "This Month" },
            { key: "current_year", label: "Current Year" },
            { key: "financial_year", label: "Financial Year" },
          ].map((range) => (
            <MenuItem
              key={range.key}
              onClick={() => {
                setFilters((prev) => ({ ...prev, date_range: range.key }));
                setCustomDateRange({ from: "", to: "" });
                setDateMenuAnchor(null);
                setShowCustomDateInputs(false);
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  width: "100%",
                }}
              >
                <Typography variant="body2">{range.label}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {getDateRangeDisplay(range.key)}
                </Typography>
              </Box>
            </MenuItem>
          ))}

          <Divider />

          {/* ✅ Custom Date Range - Expandable */}
          <MenuItem
            onClick={() => setShowCustomDateInputs(!showCustomDateInputs)}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              bgcolor: showCustomDateInputs ? "#e3f2fd" : "inherit",
            }}
          >
            <Typography
              variant="body2"
              sx={{ fontWeight: showCustomDateInputs ? 600 : 400 }}
            >
              Custom Date Range
            </Typography>
            {showCustomDateInputs ? <ExpandLess /> : <ExpandMore />}
          </MenuItem>

          {/* ✅ Both Date Pickers Together */}
          <Collapse in={showCustomDateInputs} timeout="auto" unmountOnExit>
            <Box
              sx={{
                px: 2,
                py: 2,
                bgcolor: "#f5f5f5",
                borderTop: "1px solid #e0e0e0",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mb: 1, display: "block" }}
              >
                Select Date Range
              </Typography>

              {/* From and To Date Inputs Side by Side */}
              <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                <TextField
                  label="From Date"
                  type="date"
                  size="small"
                  value={tempCustomDateRange.from}
                  onChange={(e) =>
                    setTempCustomDateRange((prev) => ({
                      ...prev,
                      from: e.target.value,
                    }))
                  }
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    max:
                      tempCustomDateRange.to ||
                      new Date().toISOString().split("T")[0],
                  }}
                  sx={{
                    flex: 1,
                    "& .MuiInputBase-root": {
                      height: 40,
                      fontSize: "0.75rem",
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: "0.75rem",
                    },
                  }}
                />

                <TextField
                  label="To Date"
                  type="date"
                  size="small"
                  value={tempCustomDateRange.to}
                  onChange={(e) =>
                    setTempCustomDateRange((prev) => ({
                      ...prev,
                      to: e.target.value,
                    }))
                  }
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    min: tempCustomDateRange.from || undefined,
                    max: new Date().toISOString().split("T")[0],
                  }}
                  sx={{
                    flex: 1,
                    "& .MuiInputBase-root": {
                      height: 40,
                      fontSize: "0.75rem",
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: "0.75rem",
                    },
                  }}
                />
              </Box>

              {/* Selected Range Preview */}
              {tempCustomDateRange.from && tempCustomDateRange.to && (
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    mb: 1.5,
                    p: 1,
                    bgcolor: "#e8f5e9",
                    borderRadius: 1,
                    textAlign: "center",
                    color: "#2e7d32",
                    fontWeight: 500,
                  }}
                >
                  📅 {formatDateDisplay(tempCustomDateRange.from)} to{" "}
                  {formatDateDisplay(tempCustomDateRange.to)}
                </Typography>
              )}

              {/* Apply and Cancel Buttons */}
              <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleCancelCustomDateRange}
                  sx={{
                    fontSize: "0.7rem",
                    textTransform: "none",
                    borderRadius: 1,
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleApplyCustomDateRange}
                  disabled={
                    !tempCustomDateRange.from || !tempCustomDateRange.to
                  }
                  sx={{
                    fontSize: "0.7rem",
                    textTransform: "none",
                    borderRadius: 1,
                  }}
                >
                  Apply
                </Button>
              </Box>
            </Box>
          </Collapse>
        </Menu>
      </Box>

      <Box id="print-section">
        <InfiniteScroll
          dataLength={indents.length}
          next={loadMore}
          hasMore={hasMore}
          loader={<h4>Loading...</h4>}
          // height={"70vh"}
        >
          {isMobile ? (
            /* 📱 MOBILE VIEW - Fixed Card Design */
            <Box sx={{ bgcolor: "background.paper" }}>
              {filteredIndents.map((indent, index) => (
                <Box key={indent.indent_id || index}>
                  {/* Header Card */}
                  <Box
                    onClick={() =>
                      setExpandedIndent(
                        expandedIndent === indent.indent_id
                          ? null
                          : indent.indent_id,
                      )
                    }
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      p: 1.5,
                      cursor: "pointer",
                      bgcolor: index % 2 === 0 ? "#fafafa" : "#ffffff",
                      "&:active": {
                        bgcolor: "#e3f2fd",
                      },
                      borderLeft: `4px solid ${
                        indent.status === "Approved"
                          ? "#4caf50"
                          : indent.status === "Rejected"
                            ? "#f44336"
                            : "#ff9800"
                      }`,
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 0.5,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "0.7rem",
                            color: "#666",
                            minWidth: 25,
                          }}
                        >
                          #{index + 1}
                        </Typography>
                        <Typography
                          sx={{
                            fontWeight: "bold",
                            fontSize: "0.85rem",
                            flex: 1,
                          }}
                        >
                          {indent.indent_no}
                        </Typography>
                      </Box>

                      <Typography
                        sx={{
                          fontSize: "0.75rem",
                          color: "#666",
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <span style={{ fontWeight: 500 }}>
                          {indent.project_name?.substring(0, 20) || "N/A"}
                        </span>
                        <span style={{ color: "#999" }}>•</span>
                        <span style={{ fontSize: "0.7rem" }}>
                          {indent.indent_type || "N/A"}
                        </span>
                      </Typography>
                    </Box>

                    <IconButton size="small" sx={{ ml: 1 }}>
                      {expandedIndent === indent.indent_id ? (
                        <ExpandLess fontSize="small" />
                      ) : (
                        <ExpandMore fontSize="small" />
                      )}
                    </IconButton>
                  </Box>

                  {/* ✅ Expanded Details - FIXED DESIGN */}
                  <Collapse
                    in={expandedIndent === indent.indent_id}
                    timeout="auto"
                    unmountOnExit
                  >
                    <Box
                      sx={{
                        px: 2,
                        py: 1.5,
                        bgcolor: index % 2 === 0 ? "#f8f9fa" : "#fafafa",
                        borderLeft: `4px solid ${
                          indent.status === "Approved"
                            ? "#4caf50"
                            : indent.status === "Rejected"
                              ? "#f44336"
                              : "#ff9800"
                        }`,
                      }}
                    >
                      {/* Details Grid */}
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          gap: 1.2,
                          mb: 1.5,
                        }}
                      >
                        {/* Project - Full Width */}
                        <Box sx={{ gridColumn: "1 / -1" }}>
                          <Typography
                            sx={{
                              fontSize: "0.65rem",
                              color: "#999",
                              letterSpacing: "0.5px",
                              mb: 0.3,
                            }}
                          >
                            PROJECT
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.8rem",
                              fontWeight: 500,
                              color: "#333",
                            }}
                          >
                            {indent.project_name || "N/A"}
                          </Typography>
                        </Box>

                        {/* Division */}
                        <Box>
                          <Typography
                            sx={{
                              fontSize: "0.65rem",
                              color: "#999",
                              letterSpacing: "0.5px",
                              mb: 0.3,
                            }}
                          >
                            DIVISION
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.75rem",
                              fontWeight: 500,
                              color: "#555",
                            }}
                          >
                            {indent.division_name || "N/A"}
                          </Typography>
                        </Box>

                        {/* Indent Type */}
                        <Box>
                          <Typography
                            sx={{
                              fontSize: "0.65rem",
                              color: "#999",
                              letterSpacing: "0.5px",
                              mb: 0.3,
                            }}
                          >
                            INDENT TYPE
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.75rem",
                              fontWeight: 500,
                              color: "#555",
                            }}
                          >
                            {indent.indent_type || "N/A"}
                          </Typography>
                        </Box>

                        {/* Created By */}
                        <Box>
                          <Typography
                            sx={{
                              fontSize: "0.65rem",
                              color: "#999",
                              letterSpacing: "0.5px",
                              mb: 0.3,
                            }}
                          >
                            CREATED BY
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.75rem",
                              fontWeight: 500,
                              color: "#555",
                            }}
                          >
                            {indent.name || "N/A"}
                          </Typography>
                        </Box>

                        {/* Approved By */}
                        {indent.approved_name && (
                          <Box>
                            <Typography
                              sx={{
                                fontSize: "0.65rem",
                                color: "#999",
                                letterSpacing: "0.5px",
                                mb: 0.3,
                              }}
                            >
                              APPROVED BY
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.75rem",
                                fontWeight: 500,
                                color: "#555",
                              }}
                            >
                              {indent.approved_name}
                            </Typography>
                          </Box>
                        )}

                        {/* Created Date */}
                        <Box>
                          <Typography
                            sx={{
                              fontSize: "0.65rem",
                              color: "#999",
                              letterSpacing: "0.5px",
                              mb: 0.3,
                            }}
                          >
                            CREATED DATE
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.75rem",
                              fontWeight: 500,
                              color: "#555",
                            }}
                          >
                            {formatOnlyDate(indent.created_at)}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Remarks */}
                      {indent.remarks && (
                        <Box sx={{ mb: 1.5 }}>
                          <Typography
                            sx={{
                              fontSize: "0.65rem",
                              color: "#999",
                              letterSpacing: "0.5px",
                              mb: 0.3,
                            }}
                          >
                            REMARKS
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.75rem",
                              color: "#555",
                              fontStyle: "italic",
                              lineHeight: 1.4,
                            }}
                          >
                            {indent.remarks}
                          </Typography>
                        </Box>
                      )}

                      <Divider sx={{ my: 1.5, borderColor: "#ddd" }} />

                      {/* Action Buttons */}
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        {/* Approve/Reject */}
                        {userRole != "Staff" && (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.8,
                              bgcolor:
                                indent.status === "Approved"
                                  ? "#e8f5e9"
                                  : indent.status === "Rejected"
                                    ? "#ffebee"
                                    : "#fff3e0",
                              px: 1.5,
                              py: 0.8,
                              borderRadius: "8px",
                              border: `1px solid ${
                                indent.status === "Approved"
                                  ? "#4caf50"
                                  : indent.status === "Rejected"
                                    ? "#f44336"
                                    : "#ff9800"
                              }`,
                            }}
                          >
                            <Checkbox
                              checked={
                                indent.status === "Approved" ||
                                indent.status === "Rejected"
                              }
                              onChange={(e) => {
                                e.stopPropagation();
                                setApproveAnchorEl(e.currentTarget);
                                setIndentToApprove(indent);
                              }}
                              sx={{
                                padding: 0,
                                transform: "scale(0.9)",
                                color:
                                  indent.status === "Approved"
                                    ? "#4caf50"
                                    : indent.status === "Rejected"
                                      ? "#f44336"
                                      : "#ff9800",
                                "&.Mui-checked": {
                                  color:
                                    indent.status === "Approved"
                                      ? "#4caf50"
                                      : "#f44336",
                                },
                              }}
                            />
                            <Typography
                              sx={{
                                fontSize: "0.75rem",
                                fontWeight: 600,
                                color:
                                  indent.status === "Approved"
                                    ? "#2e7d32"
                                    : indent.status === "Rejected"
                                      ? "#c62828"
                                      : "#f57c00",
                              }}
                            >
                              {indent.status === "Approved"
                                ? "Approved"
                                : indent.status === "Rejected"
                                  ? "Rejected"
                                  : "Approve"}
                            </Typography>
                          </Box>
                        )}

                        {/* Edit, Print, Delete Buttons */}
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEdit(indent);
                            }}
                            disabled={indent.status === "Approved"}
                            sx={{
                              bgcolor: "#e3f2fd",
                              borderRadius: "8px",
                              width: 36,
                              height: 36,
                              "&:hover": {
                                bgcolor: "#bbdefb",
                                transform: "scale(1.05)",
                              },
                              "&:disabled": {
                                bgcolor: "#f5f5f5",
                                opacity: 0.5,
                              },
                              transition: "all 0.2s",
                            }}
                          >
                            <EditIcon
                              sx={{ fontSize: "1.1rem", color: "#1976d2" }}
                            />
                          </IconButton>

                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePrintIndent(indent);
                            }}
                            sx={{
                              bgcolor: "#f3e5f5",
                              borderRadius: "8px",
                              width: 36,
                              height: 36,
                              "&:hover": {
                                bgcolor: "#e1bee7",
                                transform: "scale(1.05)",
                              },
                              transition: "all 0.2s",
                            }}
                          >
                            <PrintIcon
                              sx={{ fontSize: "1.1rem", color: "#7b1fa2" }}
                            />
                          </IconButton>

                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setAnchorEl(e.currentTarget);
                              setIndentToDelete(indent.indent_id);
                            }}
                            disabled={indent.status === "Approved"}
                            sx={{
                              bgcolor: "#ffebee",
                              borderRadius: "8px",
                              width: 36,
                              height: 36,
                              "&:hover": {
                                bgcolor: "#ffcdd2",
                                transform: "scale(1.05)",
                              },
                              "&:disabled": {
                                bgcolor: "#f5f5f5",
                                opacity: 0.5,
                              },
                              transition: "all 0.2s",
                            }}
                          >
                            <DeleteIcon
                              sx={{ fontSize: "1.1rem", color: "#d32f2f" }}
                            />
                          </IconButton>
                        </Box>
                      </Box>
                    </Box>
                  </Collapse>

                  <Divider />
                </Box>
              ))}
            </Box>
          ) : (
            /* 💻 DESKTOP VIEW - UNCHANGED */
            <TableContainer
              component={Paper}
              sx={{
                maxHeight: "88vh",
                overflow: "auto",
              }}
            >
              <Table
                stickyHeader
                size="small"
                sx={{
                  minWidth: 1600,
                  "& td, & th": {
                    textAlign: "center",
                    whiteSpace: "nowrap",
                    borderBottom: "1px solid #ccc",
                     padding: "0px 16px",
                  },
                }}
              >
                <TableHead>
                  <TableRow
                    sx={{
                      backgroundColor: "#f5f5f5",
                      "& th": {
                        fontWeight: "bold",
                        color: "#333",
                        borderBottom: "1px solid #999",
                        backgroundColor: "#f5f5f5",
                        fontSize: "0.75rem",
                      },
                    }}
                  >
                    {userRole != "Staff" && (
                      <TableCell sx={{ padding: "0px" }}>Approve</TableCell>
                    )}

                    <TableCell>S. No.</TableCell>

                    <TableCell
                      sx={{
                        position: "sticky",
                        left: 0,
                        backgroundColor: "#f5f5f5",
                        zIndex: 4,
                        minWidth: 50,
                      }}
                    >
                      Indent No.
                    </TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Project</TableCell>

                    <TableCell>Division</TableCell>
                    <TableCell>Indent Type</TableCell>
                    {/* <TableCell>Item Type</TableCell>
                    <TableCell>Size</TableCell> */}
                    <TableCell>Created By</TableCell>

                    <TableCell>Approved By</TableCell>
                    <TableCell>Created Date</TableCell>
                    <TableCell>Remarks</TableCell>
                    <TableCell
                      className="no-print"
                      sx={{
                        position: "sticky",
                        right: 0,
                        backgroundColor: "#f5f5f5",
                        zIndex: 3,
                      }}
                    >
                      Action
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {filteredIndents.map((indent, index) => (
                    <TableRow
                      hover
                      key={index}
                      sx={{
                        backgroundColor:
                          index % 2 === 0 ? "#fafafa" : "#ffffff",
                        "&:hover": {
                          backgroundColor: "#e3f2fd",
                          "& td": {
                            backgroundColor: "#e3f2fd !important",
                          },
                        },
                        "& td": {
                          padding: "0px",
                          fontSize: "0.7rem",
                          whiteSpace: "nowrap",
                          color: (theme) =>
                            theme.palette.mode === "dark" ? "white" : "inherit",
                          backgroundColor: (theme) =>
                            theme.palette.mode === "dark" ? "black" : "inherit",
                        },
                      }}
                    >
                      {userRole != "Staff" && (
                        <TableCell>
                          <Checkbox
                            checked={
                              indent.status === "Approved" ||
                              indent.status === "Rejected"
                            }
                            onChange={(e) => {
                              setApproveAnchorEl(e.currentTarget);
                              setIndentToApprove(indent);
                            }}
                            sx={{
                              size: "small",
                              padding: 0,
                              paddingLeft: 1,
                              paddingRight: 1,
                              transform: "scale(0.6)",
                              color:
                                indent.status === "Approved"
                                  ? "green"
                                  : indent.status === "Rejected"
                                    ? "red"
                                    : "grey",
                              "&.Mui-checked": {
                                color:
                                  indent.status === "Approved"
                                    ? "green"
                                    : "red",
                              },
                            }}
                          />
                        </TableCell>
                      )}

                      <TableCell>{index + 1}</TableCell>

                      <TableCell
                        sx={{
                          position: "sticky",
                          left: 0,
                          backgroundColor: "#fafafa",
                          zIndex: 3,
                          minWidth: 50,
                        }}
                      >
                        {indent.indent_no}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={
                            indent.status === "Approved"
                              ? "Approved"
                              : indent.status === "Rejected"
                                ? "Rejected"
                                : "Pending"
                          }
                          color={
                            indent.status === "Approved"
                              ? "success"
                              : indent.status === "Rejected"
                                ? "error"
                                : "warning"
                          }
                          variant="outlined"
                          size="small"
                          sx={{
                            height: "10px",
                            transform: "scale(0.8)",
                            "& .MuiChip-label": {
                              padding: "0 6px",
                            },
                          }}
                        />
                      </TableCell>
                      <TableCell>{indent.project_name}</TableCell>
                      <TableCell>{indent.division_name}</TableCell>
                      <TableCell>{indent.indent_type}</TableCell>
                      {/* <TableCell>
                        {indent.items.map(item => item.item_type_name)}
                      </TableCell>
                      <TableCell>
                        {indent.items.map(item => item.size_name)}
                      </TableCell> */}
                      <TableCell>{indent.name}</TableCell>

                      <TableCell>{indent.approved_name}</TableCell>
                      <TableCell>{formatOnlyDate(indent.created_at)}</TableCell>
                      <TableCell>{indent.remarks}</TableCell>
                      <TableCell
                        className="no-print"
                        sx={{
                          position: "sticky",
                          right: 0,
                          backgroundColor: "#fafafa",
                          zIndex: 2,
                        }}
                      >
                        <IconButton
                          sx={{ padding: 0, paddingLeft: 1, paddingRight: 1 }}
                          onClick={() => handleEdit(indent)}
                          disabled={indent.status === "Approved"}
                          color="primary"
                          size="small"
                        >
                          <EditIcon transform="scale(0.6)" />
                        </IconButton>

                        <IconButton
                          sx={{ padding: 0, paddingLeft: 1, paddingRight: 1 }}
                          onClick={() => handlePrintIndent(indents[index])}
                          size="small"
                        >
                          <PrintIcon transform="scale(0.6)" />
                        </IconButton>

                        <IconButton
                          sx={{ padding: 0, paddingLeft: 1, paddingRight: 1 }}
                          onClick={(e) => {
                            setAnchorEl(e.currentTarget);
                            setIndentToDelete(indent.indent_id);
                          }}
                          disabled={indent.status === "Approved"}
                          color="error"
                          size="small"
                        >
                          <DeleteIcon transform="scale(0.6)" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </InfiniteScroll>

        <ToastContainer position="top-right" autoClose={3000} />

        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={() => {
            setAnchorEl(null);
            setIndentToDelete(null);
          }}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          transformOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Box sx={{ p: 2 }}>
            <Typography variant="body2">Are you sure you ?</Typography>
            <Box
              sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}
            >
              <Button
                color="error"
                size="small"
                onClick={() => {
                  if (indentToDelete) {
                    handleDelete(indentToDelete);
                    setAnchorEl(null);
                    setIndentToDelete(null);
                  } else {
                    toast.error("Indent ID is missing.");
                  }
                }}
              >
                Yes
              </Button>

              <Button
                size="small"
                onClick={() => {
                  setAnchorEl(null);
                  setIndentToDelete(null);
                }}
              >
                No
              </Button>
            </Box>
          </Box>
        </Popover>

        {/* APPROVE/REJECT PREVIEW POPOVER */}
        <Popover
          open={Boolean(approveAnchorEl)}
          anchorEl={approveAnchorEl}
          onClose={() => {
            setApproveAnchorEl(null);
            setIndentToApprove(null);
            setRemarkInput("");
          }}
          anchorReference="anchorPosition"
          anchorPosition={{
            top: window.innerHeight / 2,
            left: window.innerWidth / 2,
          }}
          transformOrigin={{ vertical: "center", horizontal: "center" }}
          PaperProps={{
            sx: {
              width: { xs: "95%", md: "90%", lg: 1100 },
              maxWidth: 1100,
              maxHeight: "90vh",
              display: "flex",
              flexDirection: "column",
            },
          }}
        >
          <Box
            sx={{ display: "flex", flexDirection: "column", height: "100%" }}
          >
            {/* === SCROLLABLE PREVIEW CONTENT === */}
            <Box sx={{ p: 2, overflowY: "auto", flex: 1 }}>
              {/* 1. HEADER (Logo & Address) */}
              <Box
                sx={{
                  border: "1px solid #000",
                  display: "flex",
                  mb: 0,
                }}
              >
                <Box
                  sx={{
                    width: 120,
                    borderRight: "1px solid #000",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 1,
                  }}
                >
                  {/* Placeholder Logo */}
                  <Box
                    sx={{
                      textAlign: "center",
                      fontWeight: "bold",
                      fontSize: "1.5rem",
                      color: "#666",
                    }}
                  >
                    <img
                      src={"/logo1.png"}
                      alt="Logo"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                      }}
                    />
                  </Box>
                </Box>
                <Box sx={{ flex: 1, textAlign: "center", p: 1 }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    K P AND GRANDSONS - RAIPUR UNIT
                  </Typography>
                  <Typography variant="caption" display="block">
                    PLOT NO 37, PHASE-II, SILTARA INDUSTRIAL AREA, RAIPUR, C.G.
                  </Typography>
                  <Typography variant="caption" display="block">
                    Email : store@kpgrandsons.com
                  </Typography>
                  <Box
                    sx={{
                      mt: 1,
                      border: "1px solid #000",
                      display: "inline-block",
                      px: 2,
                      py: 0.5,
                      fontWeight: "bold",
                      fontSize: "0.9rem",
                    }}
                  >
                    Purchase Indent
                  </Box>
                </Box>
              </Box>

              {/* 2. DETAILS GRID */}
              <Box sx={{ border: "1px solid #000", borderTop: "none", p: 1 }}>
                <Grid container spacing={1}>
                  {/* Left Column */}
                  <Grid item xs={12} md={6}>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "140px 1fr",
                        rowGap: 0.5,
                      }}
                    >
                      <Typography variant="caption" fontWeight="bold">
                        DIVISION
                      </Typography>
                      <Typography variant="caption">
                        : {indentToApprove?.division_name || "-"}
                      </Typography>

                      <Typography variant="caption" fontWeight="bold">
                        DEPARTMENT NAME
                      </Typography>
                      <Typography variant="caption">
                        : {indentToApprove?.department_name || "-"}
                      </Typography>

                      <Typography variant="caption" fontWeight="bold">
                        INDENT NO.
                      </Typography>
                      <Typography variant="caption">
                        : {indentToApprove?.indent_no || "-"}
                      </Typography>

                      <Typography variant="caption" fontWeight="bold">
                        INDENT DATE
                      </Typography>
                      <Typography variant="caption">
                        : {formatOnlyDate(indentToApprove?.created_at)}
                      </Typography>
                    </Box>
                  </Grid>

                  {/* Right Column */}
                  <Grid item xs={12} md={6}>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "140px 1fr",
                        rowGap: 0.5,
                      }}
                    >
                      <Typography variant="caption" fontWeight="bold">
                        INDENT TYPE
                      </Typography>
                      <Typography variant="caption">
                        : {indentToApprove?.indent_type || "-"}
                      </Typography>

                      <Typography variant="caption" fontWeight="bold">
                        PROJECT
                      </Typography>
                      <Typography variant="caption">
                        : {indentToApprove?.project_name || "-"}
                      </Typography>

                      <Typography variant="caption" fontWeight="bold">
                        AUTHORIZED DATE
                      </Typography>
                      <Typography variant="caption">: -</Typography>

                      <Typography variant="caption" fontWeight="bold">
                        REQUESTED BY
                      </Typography>
                      <Typography variant="caption">
                        : {indentToApprove?.name || "-"}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              {/* 3. ITEMS TABLE */}
              <Box
                sx={{
                  overflowX: "auto",
                  border: "1px solid #000",
                  borderTop: "none",
                }}
              >
                <Table
                  size="small"
                  sx={{
                    "& td, & th": {
                      borderRight: "1px solid #000",
                      borderBottom: "1px solid #000",
                      padding: "4px 8px",
                      fontSize: "0.7rem",
                    },
                    "& th": { fontWeight: "bold", backgroundColor: "#f0f0f0" },
                    "& tr:last-child td": { borderBottom: "none" },
                    "& td:last-child, & th:last-child": { borderRight: "none" },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell align="center">Sl.</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell>Sub Category</TableCell>
                      <TableCell>Item Name</TableCell>
                      <TableCell>Item Description</TableCell>
                      <TableCell align="center">Size</TableCell>
                      <TableCell align="center">UOM</TableCell>
                      <TableCell align="center">
                        Req.
                        <br />
                        Qty
                      </TableCell>
                      <TableCell align="center">
                        Stock
                        <br />
                        Qty
                      </TableCell>
                      <TableCell align="center">
                        Indent
                        <br />
                        Qty
                      </TableCell>
                      <TableCell align="right">Est. Rate</TableCell>
                      <TableCell align="right">Est. Value</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {indentToApprove?.items?.map((item, idx) => {
                      const estValue =
                        parseFloat(item.indent_qty || 0) *
                        parseFloat(item.estimated_rate || 0);
                      return (
                        <TableRow key={idx}>
                          <TableCell align="center">{idx + 1}</TableCell>
                          <TableCell>{item.category_name || "-"}</TableCell>
                          <TableCell>{item.sub_category_name || "-"}</TableCell>
                          <TableCell>{item.item_name || "-"}</TableCell>
                          <TableCell
                            sx={{ maxWidth: 200, whiteSpace: "normal" }}
                          >
                            {item.item_description || "-"}
                          </TableCell>
                          <TableCell align="center">
                            {item.size_name || "-"}
                          </TableCell>
                          <TableCell align="center">
                            {item.uom || "-"}
                          </TableCell>
                          <TableCell align="center">
                            {item.required_qty || "-"}
                          </TableCell>
                          <TableCell align="center">
                            {item.stock_qty || "0"}
                          </TableCell>
                          <TableCell align="center" sx={{ fontWeight: "bold" }}>
                            {item.indent_qty || "0"}
                          </TableCell>
                          <TableCell align="right">
                            {item.estimated_rate || "-"}
                          </TableCell>
                          <TableCell align="right">
                            {estValue
                              ? `₹${estValue.toLocaleString("en-IN", { minimumFractionDigits: 2 })}`
                              : "-"}
                          </TableCell>
                        </TableRow>
                      );
                    })}

                    {/* Total Row */}
                    <TableRow>
                      <TableCell
                        colSpan={11}
                        align="right"
                        sx={{ fontWeight: "bold" }}
                      >
                        Total Value
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: "bold" }}>
                        ₹
                        {indentToApprove?.items
                          ?.reduce(
                            (acc, item) =>
                              acc +
                              parseFloat(item.indent_qty || 0) *
                                parseFloat(item.estimated_rate || 0),
                            0,
                          )
                          .toLocaleString("en-IN", {
                            minimumFractionDigits: 2,
                          })}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Box>

              {/* 4. CREATOR REMARKS */}
              <Box
                sx={{
                  border: "1px solid #000",
                  borderTop: "none",
                  p: 1,
                  minHeight: 40,
                  bgcolor: "#fff",
                }}
              >
                <Typography variant="caption" fontWeight="bold">
                  Remark (Creator):{" "}
                </Typography>
                <Typography variant="caption" sx={{ fontStyle: "italic" }}>
                  {indentToApprove?.remarks || "-"}
                </Typography>
              </Box>

              {/* 5. SIGNATURES */}
              <Box
                sx={{
                  border: "1px solid #000",
                  borderTop: "none",
                  p: 1,
                  mt: 0,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 4,
                    mt: 1,
                  }}
                >
                  <Typography variant="caption">
                    {localStorage.getItem("username") || "User"}
                  </Typography>
                  <Typography variant="caption" fontWeight="bold">
                    For, K P AND GRANDSONS UNIT-I
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mt: 4,
                    alignItems: "flex-end",
                  }}
                >
                  <Typography
                    variant="caption"
                    fontWeight="bold"
                    sx={{ width: "33%" }}
                  >
                    Prepared By
                  </Typography>
                  <Typography
                    variant="caption"
                    fontWeight="bold"
                    sx={{ width: "33%", textAlign: "center" }}
                  >
                    Verified By
                  </Typography>
                  <Typography
                    variant="caption"
                    fontWeight="bold"
                    sx={{ width: "33%", textAlign: "right" }}
                  >
                    Authorized Signatory
                  </Typography>
                </Box>
              </Box>
            </Box>
            {/* === END SCROLLABLE CONTENT === */}

            {/* === 6. FIXED ACTION AREA (Single Line) === */}
            <Box
              sx={{
                p: 2,
                borderTop: "1px solid #ddd",
                bgcolor: "#f5f5f5",
                display: "flex",
                flexDirection: { xs: "column", sm: "row" }, // Stack on mobile, Row on desktop
                alignItems: "center",
                gap: 2,
                boxShadow: "0px -2px 10px rgba(0,0,0,0.05)",
              }}
            >
              {/* Approver Remark Input Field (Flex grow to fill space) */}
              <TextField
                placeholder="Enter Approver's Remark..."
                size="small"
                value={remarkInput}
                onChange={(e) => setRemarkInput(e.target.value)}
                sx={{
                  flex: 1,
                  width: { xs: "100%", sm: "auto" },
                  bgcolor: "white",
                }}
                multiline
                maxRows={2}
              />

              {/* Action Buttons (Grouped) */}
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  width: { xs: "100%", sm: "auto" },
                  justifyContent: { xs: "flex-end", sm: "flex-start" },
                }}
              >
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => {
                    setApproveAnchorEl(null);
                    setIndentToApprove(null);
                    setRemarkInput("");
                  }}
                >
                  Close
                </Button>

                <Button
                  variant="contained"
                  color="error"
                  onClick={() => {
                    if (!remarkInput.trim()) {
                      toast.warning("Please enter a remark before rejecting.");
                      return;
                    }
                    handleApprove({
                      ...indentToApprove,
                      status: "Rejected",
                      remarks: remarkInput,
                    });
                    setApproveAnchorEl(null);
                    setIndentToApprove(null);
                    setRemarkInput("");
                  }}
                  disabled={indentToApprove?.status === "Approved"}
                >
                  Reject
                </Button>

                <Button
                  variant="contained"
                  color="success"
                  onClick={() => {
                    handleApprove({
                      ...indentToApprove,
                      status: "Approved",
                      remarks: remarkInput,
                    });
                    setApproveAnchorEl(null);
                    setIndentToApprove(null);
                    setRemarkInput("");
                  }}
                  disabled={indentToApprove?.status === "Approved"}
                >
                  Approve
                </Button>
              </Box>
            </Box>
          </Box>
        </Popover>
      </Box>
    </Box>
  );
};

export default IndentList;
